import ExcelJS from 'exceljs';
import Papa from 'papaparse';
import { DataType, MasterDataTable, ResultsType, TableType } from '../@types/store';
import { useGlobalStore } from '../store/useGlobalStore';
import { handleFieldDetails, handleFieldValues } from '../actions/RepositoriesActions/dmcActions';
import { BannerTypes } from '../components/Banner';

export const inApp = (): boolean => {
  if (typeof window !== "undefined") {
    let parts = window.location.pathname.split("/app/")
    if (parts.length > 1)
      return true
  }
  return false
}

export const getCurrentPath = (): string => {
  if (typeof window !== "undefined") {
    let parts = window.location.pathname.split("/app/")
    if (parts.length > 1)
      return parts[1]
  }
  return ''
}

export const getCookieByValue = (name: string): string => {
  if (typeof window !== "undefined") {
    let cookies: { [key: string]: string } = {};
    window.document.cookie.split(';').forEach(function (el) {
      let [key, value] = el.split('=');
      cookies[key.trim()] = value;
    })
    return cookies[name];
  }
  return ''
}

export const getErrorMessage = (error: any): string => {
  return error?.response?.data?.detail ?? error?.response?.data?.error ?? error?.message ?? 'Please try later'
}

export const getErrorTraceback = (error: any): string[] => {
  return error?.response?.data?.traceback && Array.isArray(error?.response?.data?.traceback) ?
    error?.response?.data?.traceback.map((t: { errorDescription: string }) => t.errorDescription)
    :
    []
}

export const formatMasterColumns = (tables: MasterDataTable[]): DataType[] => {
  return tables.map((t, i) => {
    return {
      key: t.key,
      label: t.name,
      synchro: t.synchro,
      data: { columns: [], data: [], next: false, previous: false, count: 0 }
    };
  })
}

export const formatMasterData = async (data: any, key: string): Promise<TableType> => {
  const columns = data.results.length > 0 ? await Promise.all(Object.keys(data.results[0])
    .filter(r => r !== 'id')
    .map(async (c) => {
      if (!["created_at", "updated_at"].includes(c)) {
        let qs = generateQueryString({ field: c, table: key });
        let res = await handleFieldDetails(qs);
        let col = { title: capitalize(c), dataIndex: c, key: c }
        if (res.data_format === "BOOL") {
          if (res?.nature && res?.nature.includes("MD_")) {
            let values = await handleFieldValues(res.nature)
            return { ...col, values: values, type: 'boolean' }
          }
          return { ...col, type: 'boolean' }
        }
      }
      return { title: capitalize(c), dataIndex: c, key: c }
    }))
    :
    useGlobalStore.getState().masterTables.find(m => m.key === key)?.data.columns

  const formattedColumns = columns && columns.length > 0 ? [...columns.filter(c => !["created_at", "updated_at"].includes(c.dataIndex)), ...columns.filter(c => ["created_at", "updated_at"].includes(c.dataIndex))] : []
  const formattedData = data.results.map((d: { id: any; created_at: Date; updated_at: Date; }) => { const { id, created_at, updated_at, ...rest } = d; return { ...rest, created_at: (new Date(d.created_at)).toLocaleString("fr-FR"), updated_at: (new Date(d.updated_at)).toLocaleString("fr-FR"), key: d.id } });
  return { columns: formattedColumns, data: formattedData, next: data.next, previous: data.previous, count: data.count };
};

export const capitalize = (string: string): string => {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export const formatUsersData = (data: any): any => {
  const formattedData = data.results.map((d: {
    last_name: any
    first_name: any
    id: any
    email: any
  }) => d)
  return { data: formattedData, next: data.next, previous: data.previous, count: data.count };
}

const isXlsxBuffer = (data: ArrayBuffer | Uint8Array): boolean => {
  // xlsx files are ZIP archives and start with the PK magic bytes: 50 4B 03 04
  const bytes = data instanceof Uint8Array ? data : new Uint8Array(data);
  return bytes[0] === 0x50 && bytes[1] === 0x4B && bytes[2] === 0x03 && bytes[3] === 0x04;
};

export const getXlsxFileData = async (data: ArrayBuffer | Uint8Array): Promise<any> => {
  const workbook = new ExcelJS.Workbook();

  const worksheetToJson = (ws: ExcelJS.Worksheet): Record<string, any>[] => {
    const headers: string[] = [];
    const rows: Record<string, any>[] = [];
    ws.eachRow((row: ExcelJS.Row, rowNumber: number) => {
      if (rowNumber === 1) {
        row.eachCell((cell: ExcelJS.Cell, col: number) => { headers[col - 1] = String(cell.value ?? ''); });
      } else {
        const obj: Record<string, any> = {};
        row.eachCell((cell: ExcelJS.Cell, col: number) => { obj[headers[col - 1]] = cell.value; });
        rows.push(obj);
      }
    });
    return rows;
  };

  if (isXlsxBuffer(data)) {
    const buffer = data instanceof Uint8Array ? data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength) : data;
    await workbook.xlsx.load(buffer as ArrayBuffer);
    if (workbook.worksheets.length > 1) {
      const sheets: { [key: string]: any[] } = {};
      workbook.worksheets.forEach((ws: ExcelJS.Worksheet) => { sheets[ws.name] = worksheetToJson(ws); });
      return sheets;
    }
    return worksheetToJson(workbook.worksheets[0]);
  } else {
    // CSV (semicolon-delimited, as produced by the pricing engine)
    const text = new TextDecoder().decode(data);
    const result = Papa.parse<Record<string, any>>(text, { header: true, delimiter: ';', skipEmptyLines: true });
    return result.data;
  }
}

export const formatControllerData = (data: any): TableType => {
  const columns = data.results.length > 0 ? Object.keys(data.results[0]).filter(r => r !== 'id').map(c => { return { title: capitalize(c), dataIndex: c, key: c } }) : useGlobalStore.getState().controller.columns
  const formattedColumns = [...columns.filter(c => !["created_at", "updated_at"].includes(c.dataIndex)), ...columns.filter(c => ["created_at", "updated_at"].includes(c.dataIndex))]
  const formattedData = data.results.map((d: { id: any; created_at: Date; updated_at: Date; empty: boolean; is_key: boolean; }) => { const { id, created_at, updated_at, empty, is_key, ...rest } = d; return { ...rest, empty: empty ? 'TRUE' : 'FALSE', is_key: is_key ? 'TRUE' : 'FALSE', created_at: (new Date(d.created_at)).toLocaleString("fr-FR"), updated_at: (new Date(d.updated_at)).toLocaleString("fr-FR"), key: d.id } })
  return { columns: formattedColumns, data: formattedData, next: data.next, previous: data.previous, count: data.count }
}

export const formatNatureData = (data: any): TableType => {
  const columns = Object.keys(data.results[0]).filter(r => r !== 'id').map(c => { return { title: capitalize(c), dataIndex: c, key: c } })
  const formattedColumns = [...columns.filter(c => !["created_at", "updated_at"].includes(c.dataIndex)), ...columns.filter(c => ["created_at", "updated_at"].includes(c.dataIndex))]
  const formattedData = data.results.map((d: { id: any; created_at: Date; updated_at: Date; }) => { const { id, created_at, updated_at, ...rest } = d; return { ...rest, created_at: (new Date(d.created_at)).toLocaleString("fr-FR"), updated_at: (new Date(d.updated_at)).toLocaleString("fr-FR"), key: d.id } })
  return { columns: formattedColumns, data: formattedData, next: data.next, previous: data.previous, count: data.count }
}

export const formatResultsData = (data: any, key: string, downloadLink: string): ResultsType => {
  const columns = data.length > 0 ? Object.keys(data[0]).map(c => { return { title: capitalize(c), dataIndex: c, key: c } }) : []
  return { columns: columns, data: data, count: data.length, key: key, downloadLink: downloadLink }
}

export const formatSimpleData = async (data: any, key: string): Promise<TableType> => {
  const columns = data.results.length > 0 ? await Promise.all(Object.keys(data.results[0])
    .filter(r => r !== 'id')
    .map(async (c) => {
      if (!["created_at", "updated_at"].includes(c)) {
        let qs = generateQueryString({ field: c, table: key });
        let res = await handleFieldDetails(qs);
        let col = { title: capitalize(c), dataIndex: c, key: c }
        if (res?.format === "BOOL") {
          if (res?.nature && res?.nature.includes("MD_")) {
            let values = await handleFieldValues(res.nature)
            return { ...col, values: values, type: 'boolean' }
          }
          return { ...col, type: 'boolean' }
        }
        if (res?.nature && res?.nature.includes("MD_")) {
          let values = await handleFieldValues(res.nature)
          return { ...col, values: values }
        }
      }
      return { title: capitalize(c), dataIndex: c, key: c }
    }))
    :
    []

  const formattedColumns = columns && columns.length > 0 ? [...columns.filter(c => !["created_at", "updated_at"].includes(c.dataIndex)), ...columns.filter(c => ["created_at", "updated_at"].includes(c.dataIndex))] : []
  const formattedData = data.results.map((d: { id: any; created_at: Date; updated_at: Date; }) => { const { id, created_at, updated_at, ...rest } = d; return { ...rest, created_at: (new Date(d.created_at)).toLocaleString("fr-FR"), updated_at: (new Date(d.updated_at)).toLocaleString("fr-FR"), key: d.id } });
  return { columns: formattedColumns, data: formattedData, next: data.next, previous: data.previous, count: data.count }
}

export const generateQueryString = (data: object): any => {
  return Object.entries(data)
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&');
}

export const generateColors = (numColors: number, baseHue = 0) => {
  const colors = [];

  for (let i = 0; i < numColors; i++) {
    const hue = (baseHue + i * (360 / numColors)) % 360;
    const color = `hsl(${hue}, 70%, 80%)`; // High lightness for light colors
    colors.push(color);
  }

  return colors;
}

export const generateTarificationOptions = (length: number) => {
  let values = Array.from({ length: length }, (_, i) => i + 1)
  return values.map(i => { return { value: i, label: `Tarification ${i}` } })
}

export const addNodesPositions = (nodes: any[]) => {
  const gridSize = Math.ceil(Math.sqrt(nodes.length))
  const gridSpacing = 200
  return nodes.map((node, index) => ({
    ...node,
    position: {
      x: (index % gridSize) * gridSpacing,
      y: Math.floor(index / gridSize) * gridSpacing,
    },
  }))
}

export const convertJsonToCsv = async (key: string): Promise<Blob | null> => {
  const json = useGlobalStore.getState().resultsTables.find((d: any) => d.key === key)?.data
  if (!json || json.length === 0) return null

  const workbook = new ExcelJS.Workbook();
  const ws = workbook.addWorksheet(key);
  ws.columns = Object.keys(json[0]).map((k: string) => ({ header: k, key: k }));
  json.forEach((row: Record<string, any>) => ws.addRow(row));

  const csvBuffer = await workbook.csv.writeBuffer();
  return new Blob([csvBuffer], { type: "text/csv;charset=utf-8;" });
}

export const convertTaskStatusToBannerType = (status: 'PENDING' | 'IN_PROGRESS' | 'SUCCESS' | 'FAILED'): BannerTypes => {
  if (["PENDING", "IN_PROGRESS"].includes(status)) return "info"
  if (status === "SUCCESS") return "success"
  if (status === "FAILED") return "error"
  return "info"
}